/**
 * DetailLayout — 详情页通用外壳
 * 标题行（media + 标题 + 副标题 + 徽标）+ 右侧操作区（编辑/删除等）+ 内容卡片。
 * 面包屑与回退由 Layout 的 PageBreadcrumb 统一提供，本组件只负责内容区。
 */
import type { ReactNode } from 'react'
import { makeStyles, tokens, Card, Text, Spinner } from '@fluentui/react-components'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  center: {
    display: 'flex',
    justifyContent: 'center',
    padding: '80px 0',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
  },
  titleBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    minWidth: 0,
  },
  media: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '44px',
    height: '44px',
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontSize: '22px',
  },
  titleCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flexWrap: 'wrap',
  },
  title: {
    fontSize: '22px',
    fontWeight: 700,
    color: tokens.colorNeutralForeground1,
  },
  subtitle: {
    fontSize: '13px',
    color: tokens.colorNeutralForeground3,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  body: {
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
  sectionTitle: {
    fontSize: '15px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
  },
  rows: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
    alignItems: 'baseline',
    gap: '16px',
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    ':last-child': {
      borderBottom: 'none',
    },
  },
  rowFull: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: '6px',
  },
  label: {
    flexShrink: 0,
    width: '140px',
    fontSize: '13px',
    color: tokens.colorNeutralForeground2,
  },
  value: {
    flex: 1,
    minWidth: 0,
    fontSize: '14px',
    color: tokens.colorNeutralForeground1,
    wordBreak: 'break-word',
    whiteSpace: 'pre-wrap',
  },
  emptyValue: {
    color: tokens.colorNeutralForeground3,
  },
})

export interface DetailLayoutProps {
  title: ReactNode
  subtitle?: ReactNode
  badge?: ReactNode
  media?: ReactNode
  actions?: ReactNode
  loading?: boolean
  notFound?: boolean
  notFoundText?: string
  children: ReactNode
}

export default function DetailLayout({
  title,
  subtitle,
  badge,
  media,
  actions,
  loading = false,
  notFound = false,
  notFoundText = '未找到记录',
  children,
}: DetailLayoutProps) {
  const styles = useStyles()

  if (loading) {
    return (
      <div className={styles.center}>
        <Spinner size="large" />
      </div>
    )
  }

  if (notFound) {
    return (
      <Card className={styles.body}>
        <Text>{notFoundText}</Text>
      </Card>
    )
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          {media && <div className={styles.media}>{media}</div>}
          <div className={styles.titleCol}>
            <div className={styles.titleRow}>
              <Text className={styles.title}>{title}</Text>
              {badge}
            </div>
            {subtitle && <Text className={styles.subtitle}>{subtitle}</Text>}
          </div>
        </div>
        {actions && <div className={styles.actions}>{actions}</div>}
      </div>
      <Card className={styles.body}>{children}</Card>
    </div>
  )
}

/** 详情区块（带可选标题） */
export function DetailSection({
  title,
  children,
}: {
  title?: string
  children: ReactNode
}) {
  const styles = useStyles()
  return (
    <div className={styles.section}>
      {title && <Text className={styles.sectionTitle}>{title}</Text>}
      <div className={styles.rows}>{children}</div>
    </div>
  )
}

/** 键值行；full=true 时 value 在 label 下方（适合长文本/代码） */
export function DetailRow({
  label,
  value,
  full = false,
}: {
  label: string
  value: ReactNode
  full?: boolean
}) {
  const styles = useStyles()
  const isEmpty = value === null || value === undefined || value === ''
  return (
    <div className={full ? `${styles.row} ${styles.rowFull}` : styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={isEmpty ? `${styles.value} ${styles.emptyValue}` : styles.value}>
        {isEmpty ? '-' : value}
      </span>
    </div>
  )
}
